import { useMemo } from 'react';

import { createUrlWithSourceQueryParams } from '@crehana/analytics';
import { useTranslation } from '@crehana/i18n';
import CrehanaLogo from '@crehana/icons/logos/CrehanaLogo';
// biome-ignore lint/style/noRestrictedImports: legacy import kept for compatibility
import { Clamp, colors } from '@crehana/ui';
import { useMe, useUITheme } from '@crehana/web';
import NightSwitch from '@crehana/web/components/NightSwitch';
import { COMMON_THEME_VALUES } from '@crehana/web/MainMenu/themes';
// biome-ignore lint/style/noRestrictedImports: legacy import kept for compatibility
import { css } from 'styled-components';

import { USER_DASHBOARD_AMPLITUDE_PAGE_NAME } from '@/views/UserDashboard/constants';

import { SIDEBAR_WIDTH } from '../constants';
import { LOCALES } from '../i18n';
import type { RouteType } from '../types';

const { white, purple } = colors;

export interface DesktopMenuProps {
  routes: RouteType[];
  currentIndex: number;
  isNewHomeUserB2BEnabled?: boolean;
  supportsDarkMode?: boolean;
  onSelect: (value: string | number) => void;
}

const DesktopMenu: React.FC<React.PropsWithChildren<DesktopMenuProps>> = ({
  routes,
  currentIndex,
  onSelect,
  supportsDarkMode,
  isNewHomeUserB2BEnabled,
  children,
}) => {
  const { languageCode } = useTranslation();
  const STRINGS = LOCALES[languageCode];
  const { me } = useMe();
  const organizationSlug = me?.organization?.slug;
  const isB2B = !!me?.isB2B;

  const homeUrl = useMemo(
    (sourceDetail?: string) => {
      if (isNewHomeUserB2BEnabled && isB2B) {
        return createUrlWithSourceQueryParams({
          url: `/org/${organizationSlug}/learn/`,
          source: USER_DASHBOARD_AMPLITUDE_PAGE_NAME,
          sourceDetail,
        });
      }

      return organizationSlug ? `/org/${organizationSlug}/` : '/home/';
    },
    [isB2B, isNewHomeUserB2BEnabled, organizationSlug],
  );

  const { isDark, toggleTheme } = useUITheme();
  const dark = supportsDarkMode && isDark;

  return (
    <div
      className={`hidden sm:flex flex-col flex-shrink-0 h-screen overflow-y-auto ${
        dark ? 'bg-base-main' : 'bg-white'
      }`}
      css={css`
        width: ${SIDEBAR_WIDTH};
        z-index: ${COMMON_THEME_VALUES.nav.zIndex + 1};
        ${
          dark
            ? ''
            : ({ theme }) =>
                `box-shadow: 0px 1px 16px ${theme.colors.gray.main};`
        }
        transition: ${({ theme }) => theme.transition('background-color')};
      `}
    >
      <a
        className={`flex items-center justify-center py-24 border-solid border-b-2 mb-24 ${
          dark ? 'border-base-lighter-16' : 'border-gray-lighter'
        }`}
        href={homeUrl}
      >
        <CrehanaLogo size={16} color={dark ? white : purple} />
      </a>
      <div className="flex items-center px-20 mb-12">
        <span
          className={`font-label ${
            dark ? 'text-base-lighter' : 'text-gray-dark'
          }`}
        >
          {STRINGS.MENU_TITLE}
        </span>
      </div>
      {routes.map((route, i) => {
        const isSelected = currentIndex === i;
        const onClick = () => {
          onSelect(i);
        };
        let textColorClassName = dark
          ? 'text-secondary-main'
          : 'text-primary-main';

        if (!isSelected) {
          textColorClassName = 'text-gray-dark-56';
        }

        return (
          <div
            key={`desktop-button-${route.key}`}
            className={`relative ${textColorClassName}`}
            data-tour={route.tourSelector}
          >
            <div
              className={`h-24 w-4 absolute left-0 top-0 mt-12 ${
                dark ? 'bg-secondary-main' : 'bg-primary-main'
              } ${isSelected ? 'opacity-1' : 'opacity-0'}`}
              css={css`
                transition: ${({ theme }) => theme.transition('opacity')};
              `}
            />
            <button
              type="button"
              className="w-full flex items-center px-20 py-12 focus:outline-none cursor-pointer"
              onClick={onClick}
            >
              {isSelected ? (
                <route.activeIcon
                  size={24}
                  className="fill-current"
                  ignoreColor
                />
              ) : (
                <route.icon size={24} className="fill-current" ignoreColor />
              )}
              <Clamp className="font-link ml-8 break-all" clamp={1}>
                {route.label}
              </Clamp>
            </button>
          </div>
        );
      })}
      {supportsDarkMode && (
        <div
          className={`mt-12 p-24 border-solid border-t border-b ${
            dark ? 'border-base-lighter-16' : 'border-gray-light'
          }`}
          data-tour="dashboard-night-switch"
        >
          <NightSwitch
            className="w-full justify-between"
            isDark={isDark}
            toggleTheme={toggleTheme}
          />
        </div>
      )}
      {children}
    </div>
  );
};

export default DesktopMenu;
